import { isValidElement } from "react";
import { Send } from "lucide-react";
import ModalBackButton from "../components/ModalBackButton";
import PaymentMethodButton from "../components/PaymentMethodButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import EVMWalletsLogo from "../logos/EVMWalletsLogo";
import CryptoFundingSourcesLogo from "../logos/CryptoFundingSourcesLogo";
import PaymentMethodSmallButton from "../components/PaymentMethodSmallButton";
import CoinbaseLogo from "../logos/CoinbaseLogo";
import MoonPayLogo from "../logos/MoonPayLogo";
import { InteracLogo } from "../logos/InteracLogo";
import { SectionTitle } from "../components/SectionTitle";
import { minUSDAmountByPaymentMethod } from "../lib/minUSDAmountByPaymentMethod";
import Warning from "../components/Warning";
import VisaMastercardLogo from "../logos/VisaMastercardLogo";
import OnrampLogo from "../logos/OnrampLogo";
import OnramperLogo from "../logos/OnramperLogo";
import { useConnectedWallet } from "../lib/useConnectedWallet";
import { useAccount, useDisconnect } from "wagmi";
import { getAccount } from "wagmi/actions";
import { useWallets } from "../lib/connectkit/wallets/useWallets";
import { formatShortWalletAddress } from "../lib/formatShortWalletAddress";
import { useAccount as useSolanaAccount } from "../lib/solana/useAccount";
import { getWallets as getSolanaWallets } from "@wallet-standard/app";
import { glideLocalStorage } from "../lib/glideLocalStorage";
import { useEffect, useState } from "react";
import { PaymentOption } from "@paywithglide/glide-js";
import { logger } from "../lib/logger";
import { wagmiConfig } from "../lib/wagmi";
import { LogoWithBadge } from "../components/LogoWithBadge";
import { DisconnectWalletButton } from "../components/DisconnectWalletButton";
import { loadTokenSelection } from "../lib/tokenSelectionStorage";
import memStore from "../lib/memStore";
import {
  saveLastPaymentMethod,
  loadLastPaymentMethod,
} from "../lib/userPreferencesStorage";

export type PaymentMethod =
  | "wallet"
  | "external_wallet"
  | "transfer"
  | "coinbase"
  | "coinbase_app"
  | "fiat"
  | "interac"
  | "debit_card_alias"
  | "onramp"
  | "onramper";

type Props = {
  noPaymentOptions?: boolean;
  errorMessage?: string | null;
  availablePaymentMethods?: string[] | null;
  isLoadingCapabilities?: boolean;
  txAmountUSD?: number;
  selectedPaymentMethod?: PaymentMethod;
  isLoadingPaymentOptions?: boolean;
  isCreatingWalletSession?: boolean;
  onSelect: (paymentMethod: PaymentMethod | "crypto" | "onramp") => void;
  onBack?: () => void;
  onReset?: () => void;
  showAppLogo?: boolean;
  paymentOptions?: PaymentOption[];
  onChangePaymentToken?: () => void;
};

export function FundingSource({
  noPaymentOptions,
  errorMessage,
  availablePaymentMethods,
  isLoadingCapabilities,
  txAmountUSD,
  selectedPaymentMethod,
  isLoadingPaymentOptions,
  isCreatingWalletSession,
  onBack,
  onReset,
  onSelect,
  showAppLogo,
  paymentOptions,
  onChangePaymentToken,
}: Props) {
  const depositConfig = useGlideDepositConfig();
  const { isConnected, address: connectedAddress } = useConnectedWallet();
  const { connector: evmConnector, address: evmAddress } = useAccount();
  const evmWallets = useWallets();
  const { address: solanaAddress } = useSolanaAccount();
  const { disconnectAsync } = useDisconnect();
  const parentWalletAddress =
    new URLSearchParams(window.location.search).get("connectedWalletAddress") ||
    memStore.getItem("connectedWalletAddress") ||
    undefined;

  const [externalWalletEcosystem, setExternalWalletEcosystem] = useState<
    "evm" | "solana" | undefined
  >(
    localStorage.getItem("glide.externalWalletEcosystem") === "solana"
      ? "solana"
      : localStorage.getItem("glide.externalWalletEcosystem") === "evm"
        ? "evm"
        : undefined,
  );
  const [solanaConnectorName, setSolanaConnectorName] = useState<
    string | undefined
  >(localStorage.getItem("glide.solana.connector") || undefined);
  const [useExternalWallet, setUseExternalWallet] = useState(
    localStorage.getItem("glide.useExternalWallet") === "true",
  );
  const [useParentWallet, setUseParentWallet] = useState(
    localStorage.getItem("glide.useParentWallet") === "true",
  );
  const [suppressConnectedWalletView, setSuppressConnectedWalletView] =
    useState(false);

  // State for UI enhancements
  const [lastUsedMethod, setLastUsedMethod] = useState<string | null>(null);

  // Load last used method on mount
  useEffect(() => {
    const loadPreferences = async () => {
      const lastMethod = await loadLastPaymentMethod();
      setLastUsedMethod(lastMethod);
    };
    loadPreferences();
  }, []);

  useEffect(() => {
    const loadEcosystem = async () => {
      const ecosystem = await glideLocalStorage.getItem(
        "glide.externalWalletEcosystem",
      );
      setExternalWalletEcosystem(
        ecosystem === "solana"
          ? "solana"
          : ecosystem === "evm"
            ? "evm"
            : undefined,
      );

      const solanaConnector = await glideLocalStorage.getItem(
        "glide.solana.connector",
      );
      setSolanaConnectorName(solanaConnector || undefined);

      const newUseExternalWallet =
        (await glideLocalStorage.getItem("glide.useExternalWallet")) === "true";
      const newUseParentWallet =
        (await glideLocalStorage.getItem("glide.useParentWallet")) === "true";
      setUseExternalWallet(newUseExternalWallet);
      setUseParentWallet(newUseParentWallet);
    };
    loadEcosystem();
  }, []);

  useEffect(() => {
    if (useExternalWallet || useParentWallet) {
      setSuppressConnectedWalletView(false);
    }
  }, [useExternalWallet, useParentWallet]);

  // Disconnect handler
  const handleDisconnect = async () => {
    try {
      logger.info("Starting wallet disconnect...");
      setSuppressConnectedWalletView(true);
      setUseExternalWallet(false);
      setUseParentWallet(false);

      // Reset local state immediately
      setExternalWalletEcosystem(undefined);
      setSolanaConnectorName(undefined);

      await glideLocalStorage.removeItem("glide.useExternalWallet");
      await glideLocalStorage.removeItem("glide.useParentWallet");
      await glideLocalStorage.removeItem("glide.externalWalletEcosystem");
      await glideLocalStorage.removeItem("glide.solana.connector");
      await glideLocalStorage.removeItem("glide.solana.account");
      await glideLocalStorage.removeItem("glide.wagmi.recentConnectorId");

      // Disconnect wagmi
      await disconnectAsync();

      // Reset to first screen
      onReset?.();

      logger.info(
        "Wallet disconnected successfully - state should reset within 300ms",
      );
    } catch (error) {
      setSuppressConnectedWalletView(false);
      logger.error(`Failed to disconnect wallet: ${error}`);
    }
  };

  const connectedWallet = (() => {
    if (!isConnected) {
      return null;
    }

    if (suppressConnectedWalletView) {
      return null;
    }

    // External EVM wallet takes HIGHEST precedence
    if (
      (externalWalletEcosystem === "evm" || evmAddress) &&
      (connectedAddress || evmAddress)
    ) {
      const resolvedAddress = connectedAddress || evmAddress;
      if (!resolvedAddress) return null;

      // Get connector synchronously from wagmi
      const account = getAccount(wagmiConfig());
      const connector = account.connector || evmConnector;

      let name = "Wallet";
      let icon = null;

      if (connector) {
        const wallet = evmWallets.find((w) => w.connector.id === connector.id);
        name = wallet?.name || connector.name || "Wallet";
        // wallet.icon is already a React element (img tag) from useWallets
        if (wallet?.icon) {
          icon = <div className="h-8 w-8">{wallet.icon}</div>;
        }
      }

      return {
        name,
        icon,
        address: resolvedAddress,
      };
    }

    // External Solana wallet takes second precedence
    if (
      (externalWalletEcosystem === "solana" || solanaAddress) &&
      solanaAddress
    ) {
      const wallets = getSolanaWallets().get();
      const wallet = wallets.find((w) => w.name === solanaConnectorName);
      return {
        name: wallet?.name || "Solana Wallet",
        icon: wallet?.icon ? (
          <img src={wallet.icon} alt={wallet.name} className="h-8 w-8" />
        ) : null,
        address: solanaAddress,
      };
    }

    if (useParentWallet && (connectedAddress || parentWalletAddress)) {
      const resolvedAddress = connectedAddress || parentWalletAddress;
      if (!resolvedAddress) return null;

      return {
        name: depositConfig.appName || "App Wallet",
        icon: depositConfig.appFaviconUrl ? (
          <img
            src={depositConfig.appFaviconUrl}
            alt={depositConfig.appName}
            className="h-8 w-8 rounded"
          />
        ) : null,
        address: resolvedAddress,
      };
    }

    if (connectedAddress) {
      return {
        name: "Wallet",
        icon: null,
        address: connectedAddress,
      };
    }

    return null;
  })();
  const hasWalletConfigured =
    !suppressConnectedWalletView && Boolean(connectedWallet || isConnected);

  // Prefer last selected token, fallback to highest balance token
  const preferredToken = (() => {
    if (!paymentOptions || paymentOptions.length === 0) return undefined;

    // Try to load saved token selection
    const savedSelection = loadTokenSelection();
    if (savedSelection) {
      // Try to find a payment option matching the saved selection
      // Match by currency symbol (more flexible than exact ID match)
      const matchingOption = paymentOptions.find(
        (po) =>
          po.currencySymbol === savedSelection.currencySymbol ||
          po.paymentCurrency === savedSelection.currencyId,
      );

      if (matchingOption) {
        return matchingOption;
      }
    }

    // Fallback to highest balance token (first in list)
    return paymentOptions[0];
  })();

  const shouldShowLogo =
    depositConfig.showAppLogo === "always"
      ? true
      : depositConfig.showAppLogo === "never"
        ? false
        : !depositConfig.embed && showAppLogo;

  // Helper to check if a payment method is available from the backend
  const isMethodAvailable = (method: string) => {
    // If availablePaymentMethods is null (not loaded or error), allow all methods
    if (!availablePaymentMethods) return true;
    return availablePaymentMethods.includes(method);
  };

  // Wrapper to save payment method preference before calling onSelect
  const handleSelectPaymentMethod = async (
    method: PaymentMethod | "crypto" | "onramp",
  ) => {
    await saveLastPaymentMethod(method);
    onSelect(method);
  };

  // Calculate available onramp methods
  const buyTokensPaymentMethods = depositConfig.popupsBlocked
    ? []
    : depositConfig.paymentMethods
        .filter(
          (method) =>
            method === "coinbase" ||
            method === "fiat" ||
            method === "interac" ||
            method === "debit_card_alias" ||
            method === "onramp" ||
            (method === "onramper" && depositConfig.mode === "deposit"),
        )
        .filter((method) => {
          if (!txAmountUSD) return true;

          // For debit_card_alias, use the minimum from its actual alias
          const methodToCheck =
            method === "debit_card_alias"
              ? depositConfig.debitCardAlias || "fiat"
              : method;
          return txAmountUSD >= minUSDAmountByPaymentMethod[methodToCheck];
        })
        .filter(
          (method) => !depositConfig.excludeFundingSources?.includes(method),
        )
        .filter((method) => isMethodAvailable(method));

  const totalOnrampOptions = buyTokensPaymentMethods.length;
  const walletSubtitle = (() => {
    const hasBalance =
      preferredToken?.balanceUSD && Number(preferredToken.balanceUSD) > 0;

    if (isLoadingPaymentOptions) {
      return (
        <div className="h-4 w-32 rounded bg-[--color-text-secondary] opacity-20 animate-pulse" />
      );
    }

    if (noPaymentOptions && hasWalletConfigured) {
      return "No valid tokens available";
    }

    if (connectedWallet && hasBalance) {
      return `$${Number(preferredToken.balanceUSD).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${preferredToken.currencySymbol} available`;
    }

    if (!connectedWallet) {
      return "Connect your wallet and use any token";
    }

    return "";
  })();

  // Helper function to wrap payment method button with last used indicator
  const withLastUsedBadge = (method: string, button: React.ReactNode) => {
    if (method !== lastUsedMethod) return button;

    const isLoading =
      isValidElement(button) && (button.props as { loading?: boolean }).loading;

    return (
      <div className="relative inline-block w-full">
        {button}
        {!isLoading && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] text-[--color-text-secondary] font-medium uppercase tracking-wide">
              Last used
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {onBack ? <ModalBackButton onClick={() => onBack()} /> : null}

      {shouldShowLogo ? (
        <div className="mt-4 mb-4 flex flex-col items-center justify-center">
          <img
            src={depositConfig.appLogoUrl}
            className="h-[--app-logo-height] w-auto"
            alt={`${depositConfig.appName} Logo`}
          />
        </div>
      ) : null}

      {!depositConfig.showOnrampFirst ? (
        <TransactionCheckoutHeader
          title={
            depositConfig.mode === "pay"
              ? "Pay with crypto"
              : (depositConfig.mode === "deposit" &&
                    depositConfig.hideSettlementCopy) ||
                  !depositConfig.currency?.symbol
                ? "Deposit with crypto"
                : `Get ${depositConfig.currency.symbol}`
          }
        />
      ) : null}

      {noPaymentOptions ? (
        <Warning className="mb-4">
          Your wallet doesn't have any tokens to pay with. Try a different
          wallet or funding source.
        </Warning>
      ) : null}

      {errorMessage ? <Warning className="mb-4">{errorMessage}</Warning> : null}

      {
        <div
          className={
            depositConfig.showOnrampFirst && !shouldShowLogo && !onBack
              ? "mt-6 space-y-3"
              : "space-y-3"
          }
        >
          {depositConfig.showOnrampFirst && totalOnrampOptions > 0 ? (
            <>
              {/* Onramp options first */}
              {totalOnrampOptions === 1 ? (
                <>
                  {buyTokensPaymentMethods[0] === "debit_card_alias"
                    ? withLastUsedBadge(
                        "debit_card_alias",
                        <PaymentMethodButton
                          icon={<VisaMastercardLogo className="h-8 w-8" />}
                          title="Debit card"
                          subtitle="Buy instantly with debit card"
                          loading={selectedPaymentMethod === "debit_card_alias"}
                          onClick={() =>
                            handleSelectPaymentMethod("debit_card_alias")
                          }
                          disabled={depositConfig.chain?.isTestnet}
                        />,
                      )
                    : buyTokensPaymentMethods[0] === "coinbase"
                      ? withLastUsedBadge(
                          "coinbase",
                          <PaymentMethodButton
                            icon={<CoinbaseLogo className="h-6 w-6 mx-1" />}
                            title={"Coinbase"}
                            subtitle={
                              depositConfig.mode === "pay"
                                ? "Pay with ETH or USD from Coinbase"
                                : "Buy with ETH or USD from Coinbase"
                            }
                            loading={selectedPaymentMethod === "coinbase"}
                            onClick={() =>
                              handleSelectPaymentMethod("coinbase")
                            }
                            disabled={depositConfig.chain?.isTestnet}
                          />,
                        )
                      : buyTokensPaymentMethods[0] === "fiat"
                        ? withLastUsedBadge(
                            "fiat",
                            <PaymentMethodButton
                              icon={<MoonPayLogo className="h-6 w-6" />}
                              title={
                                depositConfig.copyOverrides?.[
                                  "fundingSource.moonpay.title"
                                ] || "MoonPay"
                              }
                              subtitle="Buy instantly with debit card via MoonPay"
                              loading={selectedPaymentMethod === "fiat"}
                              onClick={() => handleSelectPaymentMethod("fiat")}
                              disabled={depositConfig.chain?.isTestnet}
                            />,
                          )
                        : buyTokensPaymentMethods[0] === "interac"
                          ? withLastUsedBadge(
                              "interac",
                              <PaymentMethodButton
                                icon={<InteracLogo className="h-6 w-6" />}
                                title="Interac"
                                subtitle="Buy instantly with Interac"
                                loading={selectedPaymentMethod === "interac"}
                                onClick={() =>
                                  handleSelectPaymentMethod("interac")
                                }
                                disabled={depositConfig.chain?.isTestnet}
                              />,
                            )
                          : buyTokensPaymentMethods[0] === "onramp"
                            ? withLastUsedBadge(
                                "onramp",
                                <PaymentMethodButton
                                  icon={<OnrampLogo className="h-6 w-6" />}
                                  title="Onramp"
                                  subtitle="Buy instantly with Onramp"
                                  loading={selectedPaymentMethod === "onramp"}
                                  onClick={() =>
                                    handleSelectPaymentMethod("onramp")
                                  }
                                  disabled={depositConfig.chain?.isTestnet}
                                />,
                              )
                            : buyTokensPaymentMethods[0] === "onramper"
                              ? withLastUsedBadge(
                                  "onramper",
                                  <PaymentMethodButton
                                    icon={<OnramperLogo className="h-6 w-6" />}
                                    title="Onramper"
                                    subtitle="Buy instantly with Onramper"
                                    loading={
                                      selectedPaymentMethod === "onramper"
                                    }
                                    onClick={() =>
                                      handleSelectPaymentMethod("onramper")
                                    }
                                    disabled={depositConfig.chain?.isTestnet}
                                  />,
                                )
                              : null}
                </>
              ) : (
                <>
                  <SectionTitle
                    title={
                      depositConfig.copyOverrides?.[
                        "fundingSource.onramp.title"
                      ] ||
                      (depositConfig.mode === "pay"
                        ? "Buy crypto & pay"
                        : depositConfig.mode === "deposit" &&
                            depositConfig.hideSettlementCopy
                          ? "Deposit with fiat"
                          : `Buy ${depositConfig.currency?.symbol || "tokens"}`)
                    }
                    subtitle={
                      depositConfig.copyOverrides?.[
                        "fundingSource.onramp.subtitle"
                      ] ||
                      (depositConfig.mode === "pay"
                        ? "Pick a provider to buy crypto & pay with it"
                        : depositConfig.mode === "deposit" &&
                            depositConfig.hideSettlementCopy
                          ? "Pick a provider to deposit with fiat"
                          : `Choose a provider to buy ${depositConfig.currency?.symbol || "tokens"} instantly with fiat`)
                    }
                    centered
                  />

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {buyTokensPaymentMethods.map((method) => {
                      switch (method) {
                        case "debit_card_alias":
                          return (
                            <PaymentMethodSmallButton
                              key={method}
                              icon={<VisaMastercardLogo className="h-6 w-6" />}
                              title="Debit card"
                              loading={
                                selectedPaymentMethod === "debit_card_alias"
                              }
                              onClick={() =>
                                handleSelectPaymentMethod("debit_card_alias")
                              }
                              disabled={depositConfig.chain?.isTestnet}
                            />
                          );
                        case "coinbase":
                          return (
                            <PaymentMethodSmallButton
                              key={method}
                              icon={<CoinbaseLogo className="h-6 w-6 mx-1" />}
                              title="Coinbase"
                              loading={selectedPaymentMethod === "coinbase"}
                              onClick={() =>
                                handleSelectPaymentMethod("coinbase")
                              }
                              disabled={depositConfig.chain?.isTestnet}
                            />
                          );
                        case "fiat":
                          return (
                            <PaymentMethodSmallButton
                              key={method}
                              icon={<MoonPayLogo className="h-6 w-6" />}
                              title={
                                depositConfig.copyOverrides?.[
                                  "fundingSource.moonpay.title"
                                ] || "MoonPay"
                              }
                              loading={selectedPaymentMethod === "fiat"}
                              onClick={() => handleSelectPaymentMethod("fiat")}
                              disabled={depositConfig.chain?.isTestnet}
                            />
                          );
                        case "interac":
                          return (
                            <PaymentMethodSmallButton
                              key={method}
                              icon={<InteracLogo className="h-6 w-6" />}
                              title="Interac"
                              loading={selectedPaymentMethod === "interac"}
                              onClick={() =>
                                handleSelectPaymentMethod("interac")
                              }
                              disabled={depositConfig.chain?.isTestnet}
                            />
                          );
                        case "onramper":
                          return (
                            <PaymentMethodSmallButton
                              key={method}
                              icon={<OnramperLogo className="h-6 w-6" />}
                              title="Onramper"
                              loading={selectedPaymentMethod === "onramper"}
                              onClick={() =>
                                handleSelectPaymentMethod("onramper")
                              }
                              disabled={depositConfig.chain?.isTestnet}
                            />
                          );
                        default:
                          return null;
                      }
                    })}
                  </div>
                </>
              )}

              {/* Divider */}
              <div className="relative flex items-center">
                <div className="flex-1 border-t border-[--color-text-secondary]"></div>
                <span className="px-2 text-[--color-text-secondary] text-xs uppercase">
                  Or
                </span>
                <div className="flex-1 border-t border-[--color-text-secondary]"></div>
              </div>

              {/* Collapsed crypto option */}
              {noPaymentOptions && hasWalletConfigured ? (
                <div className="mt-1 flex gap-0.5">
                  {withLastUsedBadge(
                    "wallet",
                    <PaymentMethodButton
                      icon={
                        connectedWallet?.icon ? (
                          <LogoWithBadge
                            logo={connectedWallet.icon}
                            badgeUrl={preferredToken?.chainLogoUrl}
                            size="md"
                          />
                        ) : (
                          <EVMWalletsLogo className="h-8 w-8" />
                        )
                      }
                      title={
                        connectedWallet
                          ? formatShortWalletAddress(connectedWallet.address)
                          : "Wallet"
                      }
                      subtitle={walletSubtitle}
                      onClick={() => handleSelectPaymentMethod("wallet")}
                      loading={isCreatingWalletSession}
                    />,
                  )}
                  <DisconnectWalletButton
                    onDisconnect={handleDisconnect}
                    onChangePaymentToken={onChangePaymentToken}
                  />
                </div>
              ) : (
                withLastUsedBadge(
                  "crypto",
                  <PaymentMethodButton
                    icon={<CryptoFundingSourcesLogo className="h-8 w-8" />}
                    title="Use existing tokens"
                    subtitle={`Convert any token to ${depositConfig.currency?.symbol || "unknown"}`}
                    onClick={() => handleSelectPaymentMethod("crypto")}
                    loading={false}
                  />,
                )
              )}
            </>
          ) : (
            <>
              {/* Default order: crypto options first */}
              {depositConfig.paymentMethods.includes("wallet") &&
              !depositConfig.excludeFundingSources?.includes("wallet") &&
              isMethodAvailable("wallet") ? (
                <div className="mt-1 flex gap-0.5">
                  {withLastUsedBadge(
                    "wallet",
                    <PaymentMethodButton
                      icon={
                        connectedWallet?.icon ? (
                          <LogoWithBadge
                            logo={connectedWallet.icon}
                            badgeUrl={preferredToken?.chainLogoUrl}
                            size="md"
                          />
                        ) : (
                          <EVMWalletsLogo className="h-8 w-8" />
                        )
                      }
                      title={
                        connectedWallet
                          ? formatShortWalletAddress(connectedWallet.address)
                          : "Wallet"
                      }
                      subtitle={walletSubtitle}
                      onClick={() => handleSelectPaymentMethod("wallet")}
                      loading={isCreatingWalletSession}
                    />,
                  )}
                  {hasWalletConfigured && (
                    <DisconnectWalletButton
                      onDisconnect={handleDisconnect}
                      onChangePaymentToken={onChangePaymentToken}
                    />
                  )}
                </div>
              ) : null}
              {depositConfig.paymentMethods.includes("coinbase_app") &&
              !depositConfig.excludeFundingSources?.includes("coinbase_app") &&
              isMethodAvailable("coinbase_app") &&
              depositConfig.mode === "deposit"
                ? withLastUsedBadge(
                    "coinbase_app",
                    <PaymentMethodButton
                      icon={<CoinbaseLogo className="h-6 w-6 mx-1" />}
                      title="Coinbase"
                      subtitle={
                        depositConfig.mode === "deposit"
                          ? "Deposit from your Coinbase account"
                          : "Transfer from your Coinbase account"
                      }
                      loading={selectedPaymentMethod == "coinbase_app"}
                      onClick={() => handleSelectPaymentMethod("coinbase_app")}
                      disabled={depositConfig.chain?.isTestnet}
                    />,
                  )
                : null}
              {depositConfig.paymentMethods.includes("transfer") &&
              !depositConfig.excludeFundingSources?.includes("transfer") &&
              isMethodAvailable("transfer")
                ? withLastUsedBadge(
                    "transfer",
                    <PaymentMethodButton
                      icon={<Send className="h-6 w-6 mx-1 text-indigo-400" />}
                      title={
                        depositConfig.copyOverrides?.[
                          "fundingSource.deposit.title"
                        ] ||
                        (depositConfig.mode === "deposit"
                          ? "Deposit from anywhere"
                          : "Transfer from anywhere")
                      }
                      subtitle={
                        depositConfig.copyOverrides?.[
                          "fundingSource.deposit.subtitle"
                        ] ||
                        (depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                          ? "Pay by transferring from other sources"
                          : "Transfer from another wallet or exchange")
                      }
                      onClick={() => handleSelectPaymentMethod("transfer")}
                      loading={selectedPaymentMethod === "transfer"}
                    />,
                  )
                : null}

              {/* Onramp options after with divider */}
              {totalOnrampOptions > 0 ? (
                <div className="relative flex items-center">
                  <div className="flex-1 border-t border-[--color-text-secondary]"></div>
                  <span className="px-2 text-[--color-text-secondary] text-xs uppercase">
                    Or
                  </span>
                  <div className="flex-1 border-t border-[--color-text-secondary]"></div>
                </div>
              ) : null}

              {totalOnrampOptions === 1 ? (
                <>
                  {buyTokensPaymentMethods[0] === "debit_card_alias"
                    ? withLastUsedBadge(
                        "debit_card_alias",
                        <PaymentMethodButton
                          icon={<VisaMastercardLogo className="h-8 w-8" />}
                          title="Debit card"
                          subtitle="Buy instantly with debit card"
                          loading={selectedPaymentMethod === "debit_card_alias"}
                          onClick={() =>
                            handleSelectPaymentMethod("debit_card_alias")
                          }
                          disabled={depositConfig.chain?.isTestnet}
                        />,
                      )
                    : buyTokensPaymentMethods[0] === "coinbase"
                      ? withLastUsedBadge(
                          "coinbase",
                          <PaymentMethodButton
                            icon={<CoinbaseLogo className="h-6 w-6 mx-1" />}
                            title={"Coinbase"}
                            subtitle={
                              depositConfig.mode === "pay"
                                ? "Pay with ETH or USD from Coinbase"
                                : "Buy with ETH or USD from Coinbase"
                            }
                            loading={selectedPaymentMethod === "coinbase"}
                            onClick={() =>
                              handleSelectPaymentMethod("coinbase")
                            }
                            disabled={depositConfig.chain?.isTestnet}
                          />,
                        )
                      : buyTokensPaymentMethods[0] === "fiat"
                        ? withLastUsedBadge(
                            "fiat",
                            <PaymentMethodButton
                              icon={<MoonPayLogo className="h-6 w-6" />}
                              title={
                                depositConfig.copyOverrides?.[
                                  "fundingSource.moonpay.title"
                                ] || "MoonPay"
                              }
                              subtitle="Buy instantly with debit card via MoonPay"
                              loading={selectedPaymentMethod === "fiat"}
                              onClick={() => handleSelectPaymentMethod("fiat")}
                              disabled={depositConfig.chain?.isTestnet}
                            />,
                          )
                        : buyTokensPaymentMethods[0] === "interac"
                          ? withLastUsedBadge(
                              "interac",
                              <PaymentMethodButton
                                icon={<InteracLogo className="h-6 w-6" />}
                                title="Interac"
                                subtitle="Buy instantly with Interac"
                                loading={selectedPaymentMethod === "interac"}
                                onClick={() =>
                                  handleSelectPaymentMethod("interac")
                                }
                                disabled={depositConfig.chain?.isTestnet}
                              />,
                            )
                          : buyTokensPaymentMethods[0] === "onramp"
                            ? withLastUsedBadge(
                                "onramp",
                                <PaymentMethodButton
                                  icon={<OnrampLogo className="h-6 w-6" />}
                                  title="Onramp"
                                  subtitle="Buy instantly with Onramp"
                                  loading={selectedPaymentMethod === "onramp"}
                                  onClick={() =>
                                    handleSelectPaymentMethod("onramp")
                                  }
                                  disabled={depositConfig.chain?.isTestnet}
                                />,
                              )
                            : buyTokensPaymentMethods[0] === "onramper"
                              ? withLastUsedBadge(
                                  "onramper",
                                  <PaymentMethodButton
                                    icon={<OnramperLogo className="h-6 w-6" />}
                                    title="Onramper"
                                    subtitle="Buy instantly with Onramper"
                                    loading={
                                      selectedPaymentMethod === "onramper"
                                    }
                                    onClick={() =>
                                      handleSelectPaymentMethod("onramper")
                                    }
                                    disabled={depositConfig.chain?.isTestnet}
                                  />,
                                )
                              : null}
                </>
              ) : totalOnrampOptions > 1 ? (
                withLastUsedBadge(
                  "onramp",
                  <PaymentMethodButton
                    icon={<VisaMastercardLogo className="h-8 w-8" />}
                    title={
                      depositConfig.copyOverrides?.[
                        "fundingSource.onramp.title"
                      ] ||
                      (depositConfig.mode === "pay"
                        ? "Buy crypto & pay"
                        : depositConfig.mode === "deposit" &&
                            depositConfig.hideSettlementCopy
                          ? "Buy with fiat"
                          : `Buy ${depositConfig.currency?.symbol || "tokens"}`)
                    }
                    subtitle={
                      depositConfig.copyOverrides?.[
                        "fundingSource.onramp.subtitle"
                      ] ||
                      (depositConfig.mode === "pay"
                        ? "Pick a provider to buy crypto & pay with it"
                        : depositConfig.mode === "deposit" &&
                            depositConfig.hideSettlementCopy
                          ? "Pick a provider to deposit with fiat"
                          : `Pick a provider to buy ${depositConfig.currency?.symbol || "tokens"} instantly`)
                    }
                    onClick={() => handleSelectPaymentMethod("onramp")}
                    loading={isLoadingCapabilities}
                  />,
                )
              ) : null}
            </>
          )}
        </div>
      }
    </>
  );
}
